import { forwardRef } from 'react';
import { Typography as MuiTypography, TypographyProps as MuiTypographyProps } from '@mui/material';

/**
 * Typography component wrapper with TreePlotter theme variants.
 *
 * Restricts variants to those defined in the theme for consistency.
 * This is the foundation for all text rendering in TreePlotter.
 *
 * @example
 * ```tsx
 * <Typography variant="h1">Page Title</Typography>
 * <Typography variant="body1" color="text.secondary">Body text</Typography>
 * <Typography variant="body2" align="center" gutterBottom>
 *   Centered text with bottom margin
 * </Typography>
 * ```
 */
export interface TypographyProps extends Omit<MuiTypographyProps, 'variant'> {
  /**
   * Typography variant from the TreePlotter theme.
   * Determines the visual style and semantic meaning of the text.
   *
   * - h1: Main heading (20px, 700 weight)
   * - h2: Section heading (16px, 700 weight)
   * - h3: Subsection heading (16px, 500 weight)
   * - h4: Small heading (14px, 700 weight)
   * - h5, h6: Legacy variants (not recommended for new code)
   * - body1: Small body text (12px, 400 weight)
   * - body2: Standard body text (16px, legacy)
   * - subtitle1: Section subtitle (14px, 400 weight)
   * - subtitle2: Legacy subtitle (not recommended for new code)
   * - caption: Small labels and secondary text (10px, 400 weight)
   * - overline: Legacy emphasized text (not recommended for new code)
   * - button: Button text styling (14px, 700 weight, no text transform)
   *
   * @default 'body1'
   */
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'body1'
    | 'body2'
    | 'subtitle1'
    | 'subtitle2'
    | 'caption'
    | 'overline'
    | 'button';
}

/**
 * Typography component for consistent text rendering across TreePlotter.
 *
 * This component wraps Material UI's Typography with TreePlotter theme variants,
 * ensuring consistent typography throughout the application.
 */
export const Typography = forwardRef<HTMLElement, TypographyProps>(
  ({ variant = 'body1', ...props }, ref) => <MuiTypography ref={ref} variant={variant} {...props} />
);

Typography.displayName = 'Typography';
